// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, StateRow } from '../types';
import { num, times, esc, COL } from '../format';
import { gloss } from '../glossary';
import { tipAttr, niceMax, linePoints } from '../charts';
import { openState } from '../main';

type Mode = 'std' | 'crude';

export function renderGap(root: HTMLElement, data: Dataset): void {
  let mode: Mode = 'std';
  const c = data.meta.counts;

  root.innerHTML = `
    <div class="view-head">
      <h2>The gap a headline rate hides</h2>
      <p>Every state's imprisonment rate for ${gloss('atsi', 'Aboriginal and Torres Strait Islander')} people (ochre) sits beside its non-Indigenous rate (slate) on a ratio scale, ranked by how far apart they are. The distance between the two dots <em>is</em> the story. Switch to ${gloss('crude', 'crude')} rates and the gap barely moves — so it is not explained by the two populations having different age structures.</p>
    </div>
    <div class="controls">
      <span class="control-label">Rate basis</span>
      <div class="seg" role="tablist" aria-label="Rate basis">
        <button data-mode="std" aria-pressed="true">Age-standardised</button>
        <button data-mode="crude" aria-pressed="false">Crude</button>
      </div>
    </div>
    <div class="panel">
      <div class="panel-head"><h3>Imprisonment rate by Indigenous status, ${data.meta.latestYear}</h3><p>Per 100,000 adults, log scale. National ratio marked in the header of each basis.</p></div>
      <div class="chart-wrap" id="gap-chart"></div>
      <div class="legend">
        <span class="legend-item"><span class="legend-dot" style="background:${COL.nonIndig}"></span>Non-Indigenous</span>
        <span class="legend-item"><span class="legend-dot" style="background:${COL.atsi}"></span>Aboriginal &amp; Torres Strait Islander</span>
        <span class="legend-item" style="color:var(--text-tertiary)">Click a state to open its breakdown</span>
      </div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>The national gap over time</h3><p>Aboriginal &amp; Torres Strait Islander imprisonment as a multiple of the non-Indigenous rate, ${data.meta.firstYear}–${data.meta.latestYear}. It is getting wider, not narrower.</p></div>
      <div class="chart-wrap" id="gap-ratio"></div>
      <p class="mini-note">Age-standardised ${times(c.ratioStd2016)} (${data.meta.firstYear}) → ${times(c.ratioStd)} (${data.meta.latestYear}).</p>
    </div>
  `;

  const chart = root.querySelector('#gap-chart') as HTMLElement;
  const draw = () => drawDumbbell(chart, data, mode);
  draw();
  drawRatioLine(root.querySelector('#gap-ratio') as HTMLElement, data);

  root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      mode = b.dataset.mode as Mode;
      root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      draw();
    }),
  );
}

function drawDumbbell(el: HTMLElement, data: Dataset, mode: Mode): void {
  const nonKey = mode === 'std' ? 'nonIndigRateStd' : 'nonIndigRateCrude';
  const atsiKey = mode === 'std' ? 'atsiRateStd' : 'atsiRateCrude';
  const ratioKey = mode === 'std' ? 'ratioStd' : 'ratioCrude';

  const rows = data.states
    .filter((s) => s[atsiKey] != null && s[nonKey] != null)
    .slice()
    .sort((a, b) => (b[ratioKey] as number) - (a[ratioKey] as number));

  const W = 900, rowH = 34, top = 44, bottom = 30, m = { l: 150, r: 92 };
  const H = top + rows.length * rowH + bottom;
  const iw = W - m.l - m.r;
  const xmin = 50, xmax = 5000;
  const lx = (v: number) => m.l + ((Math.log10(Math.max(v, xmin)) - Math.log10(xmin)) / (Math.log10(xmax) - Math.log10(xmin))) * iw;
  const ticks = [50, 100, 200, 500, 1000, 2000, 5000];

  const rowSvg = (s: StateRow, i: number) => {
    const cy = top + i * rowH + rowH / 2;
    const xn = lx(s[nonKey] as number);
    const xa = lx(s[atsiKey] as number);
    const tip = `${s.name}\nAboriginal & T.S.I. ${num(s[atsiKey] as number)} per 100k\nNon-Indigenous ${num(s[nonKey] as number)} per 100k\n${times(s[ratioKey])} the non-Indigenous rate`;
    return `<g class="seg-click" ${tipAttr(tip)} data-state="${s.code}" role="button" tabindex="0" aria-label="${esc(s.name)}: ${times(s[ratioKey])} gap">
      <rect x="0" y="${cy - rowH / 2}" width="${W}" height="${rowH}" fill="transparent"/>
      <text class="axis-text" x="${m.l - 12}" y="${cy + 4}" text-anchor="end" style="font-size:12px;fill:var(--text-primary)">${esc(s.code === 'AUS' ? 'Australia' : s.name)}</text>
      <line class="dumbbell-connect" x1="${xn.toFixed(1)}" y1="${cy}" x2="${xa.toFixed(1)}" y2="${cy}"/>
      <circle cx="${xn.toFixed(1)}" cy="${cy}" r="6" fill="${COL.nonIndig}" stroke="#fff" stroke-width="1.5"/>
      <circle cx="${xa.toFixed(1)}" cy="${cy}" r="7" fill="${COL.atsi}" stroke="#fff" stroke-width="1.5"/>
      <text class="num" x="${W - m.r + 12}" y="${cy + 4}" style="font-size:12px;font-weight:600;fill:var(--accent-warm)">${times(s[ratioKey])}</text>
    </g>`;
  };

  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Imprisonment rate by Indigenous status and state">
    ${ticks.map((t) => `<line class="grid-line" x1="${lx(t).toFixed(1)}" y1="${top - 8}" x2="${lx(t).toFixed(1)}" y2="${H - bottom + 4}"/><text class="axis-text" x="${lx(t).toFixed(1)}" y="${top - 14}" text-anchor="middle">${num(t)}</text>`).join('')}
    <text class="axis-title" x="${m.l}" y="18" text-anchor="start">per 100,000 adults →</text>
    <text class="axis-text" x="${W - m.r + 12}" y="${top - 14}" text-anchor="start" style="fill:var(--accent-warm)">ratio</text>
    ${rows.map(rowSvg).join('')}
  </svg>`;

  const go = (code: string) => openState(code);
  el.querySelectorAll<SVGGElement>('[data-state]').forEach((g) => {
    g.addEventListener('click', () => go(g.dataset.state!));
    g.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') { e.preventDefault(); go(g.dataset.state!); } });
  });
}

function drawRatioLine(el: HTMLElement, data: Dataset): void {
  const r = data.rates;
  const std = r.states.AUS.stdRatio;
  const crude = r.states.AUS.crudeRatio;
  const W = 900, H = 240, m = { t: 14, r: 16, b: 28, l: 40 };
  const iw = W - m.l - m.r, ih = H - m.t - m.b;
  const maxV = niceMax(Math.max(...[...std, ...crude].filter((v): v is number => v != null)));
  const x = (i: number) => m.l + (i / (r.years.length - 1)) * iw;
  const y = (v: number) => m.t + ih - (v / maxV) * ih;
  const ticks = 4;
  const gridY = Array.from({ length: ticks + 1 }, (_, i) => (maxV / ticks) * i);
  const line = (vals: (number | null)[], col: string, label: string) =>
    `<polyline class="series-line" points="${linePoints(vals, x, y)}" stroke="${col}"/>` +
    vals.map((v, i) => (v == null ? '' : `<circle class="dot" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="6" fill="transparent" ${tipAttr(`${r.years[i]}: ${times(v)} (${label})`)} aria-label="${r.years[i]}: ${times(v)} ${esc(label)}"></circle><circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="2.6" fill="${col}"/>`)).join('');
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="National over-representation ratio over time">
    ${gridY.map((g) => `<line class="grid-line" x1="${m.l}" y1="${y(g).toFixed(1)}" x2="${W - m.r}" y2="${y(g).toFixed(1)}"/><text class="axis-text" x="${m.l - 8}" y="${y(g) + 3}" text-anchor="end">${g.toFixed(0)}×</text>`).join('')}
    ${r.years.map((yr, i) => `<text class="axis-text" x="${x(i).toFixed(1)}" y="${H - 8}" text-anchor="middle">${String(yr).slice(2)}</text>`).join('')}
    ${line(crude, COL.nonIndig, 'crude')}
    ${line(std, COL.atsi, 'age-standardised')}
    <text class="axis-text" x="${x(r.years.length - 1).toFixed(1)}" y="${y(std[std.length - 1] as number) - 8}" text-anchor="end" style="fill:${COL.atsi};font-weight:600">age-standardised</text>
    <text class="axis-text" x="${x(r.years.length - 1).toFixed(1)}" y="${y(crude[crude.length - 1] as number) + 16}" text-anchor="end" style="fill:${COL.nonIndig};font-weight:600">crude</text>
  </svg>`;
}
